import React, { useState } from 'react';

const POLICY_SLUGS = [
  { id: 'shipping', label: 'Shipping & delivery' },
  { id: 'returns', label: 'Returns & exchanges' },
  { id: 'privacy', label: 'Privacy' },
];

function Field({ id, label, value, onChange, multiline, hint }) {
  return (
    <div className="col-12">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          className="form-control"
          rows={typeof multiline === 'number' ? multiline : 3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input id={id} className="form-control" value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {hint ? <div className="form-text">{hint}</div> : null}
    </div>
  );
}

function PolicyEditor({ slug, page, onChange }) {
  const setPage = (patch) => onChange({ ...page, ...patch });

  const setSection = (index, patch) => {
    const sections = [...(page.sections || [])];
    sections[index] = { ...sections[index], ...patch };
    setPage({ sections });
  };

  const addSection = () => {
    setPage({ sections: [...(page.sections || []), { heading: '', body: '' }] });
  };

  const removeSection = (index) => {
    const sections = (page.sections || []).filter((_, i) => i !== index);
    setPage({ sections });
  };

  return (
    <div className="row g-3">
      <Field id={`policy-${slug}-title`} label="Page title" value={page.title || ''} onChange={(v) => setPage({ title: v })} />
      <Field
        id={`policy-${slug}-seo`}
        label="SEO description"
        value={page.seoDescription || ''}
        onChange={(v) => setPage({ seoDescription: v })}
        multiline={2}
      />
      {(page.sections || []).map((sec, i) => (
        <div key={`${slug}-sec-${i}`} className="col-12 border rounded p-3 bg-light">
          <SectionHeader index={i} onRemove={() => removeSection(i)} />
          <Field
            id={`policy-${slug}-h-${i}`}
            label="Section heading"
            value={sec.heading || ''}
            onChange={(v) => setSection(i, { heading: v })}
          />
          <Field
            id={`policy-${slug}-b-${i}`}
            label="Section body"
            value={sec.body || ''}
            onChange={(v) => setSection(i, { body: v })}
            multiline={4}
          />
        </div>
      ))}
      <div className="col-12">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addSection}>
          Add section
        </button>
      </div>
    </div>
  );
}

function SectionHeader({ index, onRemove }) {
  return (
    <div className="d-flex justify-content-between align-items-center mb-2">
      <span className="small fw-semibold">Section {index + 1}</span>
      <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={onRemove}>
        Remove
      </button>
    </div>
  );
}

export default function AdminSitePoliciesFields({ form, setField }) {
  const [active, setActive] = useState('shipping');
  const policies = form.policies || {};
  const storeCopy = form.store_copy || {};

  const setPolicy = (slug, page) => {
    setField('policies', { ...policies, [slug]: { ...page, slug } });
  };

  return (
    <>
      <div className="card border shadow-sm mb-3">
        <div className="card-header py-2">
          <h2 className="h6 mb-0">Policy pages</h2>
          <p className="small text-muted mb-0 mt-1">
            Shipping, returns, and privacy — linked from the footer and checkout.
          </p>
        </div>
        <div className="card-body">
          <div className="admin-site-section-nav mb-3" role="tablist">
            {POLICY_SLUGS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                className={`admin-site-section-nav__tab${active === p.id ? ' admin-site-section-nav__tab--active' : ''}`}
                onClick={() => setActive(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <PolicyEditor
            slug={active}
            page={policies[active] || { slug: active, sections: [] }}
            onChange={(page) => setPolicy(active, page)}
          />
        </div>
      </div>

      <div className="card border shadow-sm mb-3">
        <div className="card-header py-2">
          <h2 className="h6 mb-0">Policy page boilerplate</h2>
        </div>
        <div className="card-body row g-3">
          <Field
            id="policy-page-lead"
            label="Intro line under page title"
            value={storeCopy.policy_page_lead || ''}
            onChange={(v) => setField('store_copy', { ...storeCopy, policy_page_lead: v })}
          />
          <Field
            id="policy-page-footer"
            label="Footer note"
            value={storeCopy.policy_page_footer || ''}
            onChange={(v) => setField('store_copy', { ...storeCopy, policy_page_footer: v })}
            hint="Plain text at the bottom of each policy page."
          />
        </div>
      </div>
    </>
  );
}
